/**
 * TRADING BOOK — one Zerodha-style book for every product and environment:
 * Positions · Orders · Trade Book · P&L · History, scoped by Product (Futures / Options) and
 * Environment (LIVE / SHADOW / PAPER). Positions shows GroundZero's strategy positions and,
 * separately, Zerodha's own net positions.
 *
 * P&L is served entirely by GET /api/paper-book/pnl. This page NEVER calculates trading P&L: every
 * money figure, count, win rate, return % and reconciliation verdict is the backend's value; React
 * only formats, sorts and shows it.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { detailText } from "@/lib/api";
import { ConfirmSquareOffDialog } from "@/pages/PaperTrading";
import { inr, inrPnl, num, istDate } from "@/lib/format";
import { modeLabel, modeTitle } from "@/lib/modes";
import { exitText } from "@/lib/labels";
import { formatLots } from "@/lib/qty";
import { EquityChart, AnalyticsStrip, BreakdownTable } from "@/components/PnlAnalytics";
import { pnlHeadline, progressText } from "@/lib/marks";
import {
  BookOpen, CheckCircle2, XCircle, ChevronDown, ChevronRight, Search, Trophy, TrendingDown,
  Layers, ListOrdered, ScrollText, History, Wallet,
} from "lucide-react";
import OrderBook from "@/pages/OrderBook";
import TradeBook from "@/pages/TradeBook";
import BookScope, { useBookScope, scopeParams } from "@/components/BookScope";
import { futuresTradeState, toneClass } from "@/lib/tradeState";
import { visibleInterval } from "@/lib/poll";

const TABS = [
  { key: "positions", label: "Positions", icon: Layers },
  { key: "orders", label: "Orders", icon: ListOrdered },
  { key: "tradebook", label: "Trade Book", icon: ScrollText },
  { key: "pnl", label: "P&L", icon: Wallet },
  { key: "history", label: "History", icon: History },
];

const PERIODS = [
  ["TODAY", "Today"], ["YESTERDAY", "Yesterday"], ["THIS_WEEK", "This Week"],
  ["THIS_MONTH", "This Month"], ["ALL", "All time"], ["CUSTOM", "Custom"],
];


const pnlCls = (v) => (v == null ? "text-slate-500" : v > 0 ? "text-[#00C805]" : v < 0 ? "text-[#FF6b62]" : "text-slate-300");
const signed = (v) => (v == null ? "—" : inrPnl(v, 2));

function Stat({ label, value, cls, sub, testId, big, title }) {
  return (
    <div className="term-panel rounded-sm px-3 py-2 min-w-0" data-testid={testId} title={title}>
      <div className="text-[9px] uppercase tracking-wide text-slate-500 whitespace-nowrap">{label}</div>
      <div className={`${big ? "text-lg" : "text-sm"} mono font-bold leading-tight whitespace-nowrap ${cls || "text-slate-100"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 leading-tight truncate" title={typeof sub === "string" ? sub : undefined}>{sub}</div>}
    </div>
  );
}

function ReconciledBadge({ row }) {
  const rec = row.reconciliation || {};
  if (row.reconciled) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#00C805]" data-testid="reconciled-yes"
      title={`${rec.entry_pairs} entry pairs → ${rec.exit_pairs} exit pairs · ${rec.fills} fills · cash flow ${inr(rec.cash_flow_pnl, 2)}`}>
      <CheckCircle2 className="h-3.5 w-3.5" /> Reconciled</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#FF6b62]" data-testid="reconciled-no"
    title={(rec.issues || []).join("\n")}><XCircle className="h-3.5 w-3.5" /> Check</span>;
}

function ChargesCell({ value, status }) {
  if (status === "NOT_CALCULATED" || status === "NO_FILLS") {
    return <span className="text-slate-500" title="PAPER fills use the zero-charges model">{inr(value, 2)} <span className="text-[9px]">n/c</span></span>;
  }
  return <span className="text-[#FF9500]">{inr(value, 2)}</span>;
}

/* ------------------------------------------------------------------ main P&L table */
function CycleDetail({ row }) {
  const r = row.reconciliation || {};
  if (row.product === "OPTIONS") {
    return (
      <tr className="bg-[#0A0C10]" data-testid={`cycle-detail-${row.execution_id}`}>
        <td colSpan={13} className="px-4 py-2 text-[11px] mono">
          {row.direction === "SELL CE + SELL PE"
            ? <><span className="text-slate-500">SOLD</span> <span className="text-[#FF6b62]">{row.sell_contract}</span></>
            : <><span className="text-slate-500">Contracts</span> {row.buy_contract}</>}
          <span className="ml-4 text-slate-500">Allocated</span> {inr(row.allocated_capital)}
          {row.simulated && <span className="ml-4 text-[#FFCC00]">SHADOW: simulated fills</span>}
          <a className="ml-4 text-[#5b9bff] underline" href={row.open_path}>Open in Hedging</a>
        </td>
      </tr>
    );
  }
  return (
    <tr className="bg-[#0A0C10]" data-testid={`cycle-detail-${row.execution_id}`}>
      <td colSpan={13} className="px-4 py-2">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] mono">
          <span><span className="text-slate-500">Execution</span> {row.execution_id}</span>
          <span><span className="text-slate-500">Contracts</span> <span className="text-[#FF6b62]">S {row.sell_contract?.split(":").pop()}</span> → <span className="text-[#5b9bff]">B {row.buy_contract?.split(":").pop()}</span></span>
          <span><span className="text-slate-500">Entry orders</span> {r.entry_orders}</span>
          <span><span className="text-slate-500">Exit orders</span> {r.exit_orders}</span>
          <span><span className="text-slate-500">Fills</span> {r.fills}</span>
          {r.rejected_orders ? <span><span className="text-slate-500">Rejected orders</span> {r.rejected_orders}</span> : null}
          <span><span className="text-slate-500">Exit mode</span> {r.exit_granularity || "—"}</span>
          <span><span className="text-slate-500">Duplicate keys</span> {r.duplicate_keys}</span>
          <span><span className="text-slate-500">Stored realised</span> {inr(r.realised_pnl, 2)}</span>
          <span><span className="text-slate-500">Fill cash flow</span> {inr(r.cash_flow_pnl, 2)}</span>
          <span><span className="text-slate-500">Allocated</span> {inr(row.allocated_capital)}</span>
        </div>
        {(r.issues || []).length > 0 && (
          <ul className="mt-1.5 text-[11px] text-[#FF6b62] list-disc pl-5" data-testid="reconciliation-issues">
            {r.issues.map((i) => <li key={i}>{i}</li>)}
          </ul>
        )}
      </td>
    </tr>
  );
}

function CyclesTable({ cycles }) {
  const [open, setOpen] = useState(null);
  const [sort, setSort] = useState({ key: "completed_at", dir: -1 });
  const rows = useMemo(() => [...cycles].sort((a, b) => {
    const va = a[sort.key], vb = b[sort.key];
    if (va == null) return 1; if (vb == null) return -1;
    return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
  }), [cycles, sort]);
  const th = (label, key, align = "left") => (
    <th className={`px-2 py-1.5 text-${align} whitespace-nowrap ${key ? "cursor-pointer hover:text-slate-200" : ""}`}
      onClick={key ? () => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 })) : undefined}>
      {label}{sort.key === key ? (sort.dir < 0 ? " ▾" : " ▴") : ""}
    </th>
  );
  return (
    <div className="overflow-auto rounded-sm border border-[#2A2F3A]" data-testid="pnl-cycles-table">
      <table className="w-full text-[11px]">
        <thead className="bg-[#12151C] text-[10px] uppercase tracking-wide text-slate-500 sticky top-0">
          <tr>
            <th className="w-6" />
            {th("Trade", "trade_name")}{th("Underlying", "underlying")}{th("Market", "market")}
            {th("Start", "started_at")}{th("Exit", "completed_at")}{th("Levels", "levels", "right")}
            {th("Pairs", "entry_pairs", "right")}{th("Exit Reason", "exit_reason")}
            {th("Gross P&L", "gross_pnl", "right")}{th("Charges", "charges", "right")}
            {th("Net P&L", "net_pnl", "right")}{th("Reconciled", "reconciled")}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={13} className="px-3 py-6 text-center text-slate-600" data-testid="pnl-empty">No completed trades in this period.</td></tr>
          )}
          {rows.map((c) => (
            <React.Fragment key={c.execution_id}>
              <tr className="gz-row cursor-pointer" data-tone={c.reconciled ? undefined : "warning"}
                onClick={() => setOpen(open === c.execution_id ? null : c.execution_id)} data-testid={`pnl-row-${c.execution_id}`}>
                <td className="pl-2 text-slate-500">{open === c.execution_id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
                <td className="px-2 py-1.5 font-semibold text-slate-100 whitespace-nowrap">{c.trade_name}
                  {c.environment && <span className="ml-1 text-[9px] font-bold text-slate-500">{c.environment}</span>}</td>
                <td className="px-2 py-1.5 mono text-slate-200">{c.underlying}</td>
                <td className="px-2 py-1.5"><span className={`text-[9px] px-1 py-0.5 rounded-sm font-bold ${c.market === "NFO" ? "bg-[#5b9bff]/15 text-[#5b9bff]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>{c.market}</span></td>
                <td className="px-2 py-1.5 mono text-slate-400 whitespace-nowrap">{istDate(c.start_date)} {c.start_time}</td>
                <td className="px-2 py-1.5 mono text-slate-300 whitespace-nowrap">{istDate(c.exit_date)} {c.exit_time}</td>
                <td className="px-2 py-1.5 mono text-right">{c.levels}</td>
                <td className="px-2 py-1.5 mono text-right" title={`${c.entry_pairs} entry → ${c.exit_pairs} exit pairs`}>
                  {c.execution_granularity === "PAIR" ? c.entry_pairs : <span className="text-slate-600">level</span>}
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-slate-300" title={c.exit_reason || undefined}>{exitText(c.exit_reason)}</td>
                <td className={`px-2 py-1.5 mono text-right font-semibold ${pnlCls(c.gross_pnl)}`}>{signed(c.gross_pnl)}</td>
                <td className="px-2 py-1.5 mono text-right"><ChargesCell value={c.charges} status={c.charges_status} /></td>
                <td className={`px-2 py-1.5 mono text-right font-bold ${pnlCls(c.net_pnl)}`}>{signed(c.net_pnl)}</td>
                <td className="px-2 py-1.5"><ReconciledBadge row={c} /></td>
              </tr>
              {open === c.execution_id && <CycleDetail row={c} />}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupTable({ rows, first, testId }) {
  return (
    <div className="overflow-auto rounded-sm border border-[#2A2F3A]" data-testid={testId}>
      <table className="w-full text-[11px]">
        <thead className="bg-[#12151C] text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            {first.map((f) => <th key={f.label} className="px-2 py-1.5 text-left">{f.label}</th>)}
            <th className="px-2 py-1.5 text-right">Trades</th><th className="px-2 py-1.5 text-right">Won / Lost</th>
            <th className="px-2 py-1.5 text-right">Gross P&L</th><th className="px-2 py-1.5 text-right">Charges</th>
            <th className="px-2 py-1.5 text-right">Net P&L</th><th className="px-2 py-1.5 text-right">Unreconciled</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={first.length + 6} className="px-3 py-4 text-center text-slate-600">Nothing in this period.</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="gz-row">
              {first.map((f) => <td key={f.label} className="px-2 py-1.5 mono text-slate-200 whitespace-nowrap">{f.render(r)}</td>)}
              <td className="px-2 py-1.5 mono text-right">{r.completed_trades}</td>
              <td className="px-2 py-1.5 mono text-right"><span className="text-[#00C805]">{r.winners}</span> / <span className="text-[#FF6b62]">{r.losers}</span></td>
              <td className={`px-2 py-1.5 mono text-right ${pnlCls(r.gross_pnl)}`}>{signed(r.gross_pnl)}</td>
              <td className="px-2 py-1.5 mono text-right text-slate-500">{inr(r.charges, 2)}</td>
              <td className={`px-2 py-1.5 mono text-right font-bold ${pnlCls(r.net_pnl)}`}>{signed(r.net_pnl)}</td>
              <td className={`px-2 py-1.5 mono text-right ${r.unreconciled ? "text-[#FF6b62] font-bold" : "text-slate-600"}`}>{r.unreconciled}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ P&L tab */
function PnlTab({ scope }) {
  const [params, setParams] = useSearchParams();
  const period = params.get("period") || "TODAY";
  const market = params.get("market") || "ALL";
  const [q, setQ] = useState(params.get("q") || "");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [view, setView] = useState("cycle");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v == null || v === "") next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  const load = useCallback(() => {
    const p = { period, market, ...scopeParams(scope) };
    if (period === "CUSTOM") { if (!from && !to) return; if (from) p.date_from = from; if (to) p.date_to = to; }
    const needle = params.get("q");
    if (needle) p.q = needle;
    setLoading(true);
    api.get("/paper-book/pnl", { params: p })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(detailText(e, "Could not load P&L")))
      .finally(() => setLoading(false));
  }, [period, market, from, to, params, scope.product, scope.environment]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); return visibleInterval(load, 15000); }, [load]);

  const s = data?.summary;
  return (
    <div className="space-y-2" data-testid="pnl-tab">
      {/* filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="pnl-filters">
        <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]">
          {PERIODS.map(([k, label]) => (
            <button key={k} onClick={() => setParam("period", k)} data-testid={`period-${k.toLowerCase()}`}
              className={`px-2.5 py-1 text-[11px] font-bold ${period === k ? "bg-[#2563EB] text-white" : "bg-[#0A0C10] text-slate-400 hover:text-slate-200"}`}>{label}</button>
          ))}
        </div>
        {period === "CUSTOM" && (
          <div className="flex items-center gap-1 text-[11px]" data-testid="custom-range">
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setParam("from", e.target.value); }}
              className="bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-1.5 py-0.5 mono text-slate-200" />
            <span className="text-slate-500">to</span>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setParam("to", e.target.value); }}
              className="bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-1.5 py-0.5 mono text-slate-200" />
          </div>
        )}
        <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]">
          {["ALL", "MCX", "NFO"].map((m) => (
            <button key={m} onClick={() => setParam("market", m)} data-testid={`market-${m.toLowerCase()}`}
              className={`px-2.5 py-1 text-[11px] font-bold ${market === m ? "bg-[#2563EB] text-white" : "bg-[#0A0C10] text-slate-500 hover:text-slate-200"}`}>{m === "ALL" ? "All markets" : m}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setParam("q", q.trim()); }} className="flex items-center gap-1 bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2 py-0.5">
          <Search className="h-3.5 w-3.5 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => setParam("q", q.trim())} placeholder="Underlying / trade / contract"
            className="bg-transparent outline-none text-[11px] w-48 text-slate-200" data-testid="pnl-search" />
        </form>
        <span className="ml-auto text-[10px] text-slate-500 mono">
          {loading ? "Refreshing…" : data ? `${data.period.date_from ? `${istDate(data.period.date_from)} → ${istDate(data.period.date_to)}` : "All time"} · by exit date · IST` : ""}
        </span>
      </div>

      {error && <div className="text-[11px] text-[#FF6b62]" data-testid="pnl-error">{error}</div>}
      {!data && !error && <div className="text-xs text-slate-500 p-4">Loading P&L…</div>}

      {s && (
        <>
          {/* summary */}
          <div className="grid gap-1.5 grid-cols-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="pnl-summary">
            <Stat big label="Total P&L" value={signed(s.total_pnl)} cls={pnlCls(s.total_pnl)} sub="Realised net + unrealised" testId="sum-total" />
            <Stat big label="Net P&L (realised)" value={signed(s.net_pnl)} cls={pnlCls(s.net_pnl)} sub="Gross − charges" testId="sum-net" />
            <Stat label="Gross P&L" value={signed(s.gross_pnl)} cls={pnlCls(s.gross_pnl)} testId="sum-gross" />
            <Stat label="Charges" value={inr(s.charges, 2)} cls={s.charges_status === "CALCULATED" ? "text-[#FF9500]" : "text-slate-400"}
              sub={s.charges_status === "CALCULATED" ? "From fill contract notes" : (s.completed_trades ? "Not recorded on these fills" : "No completed trades")} testId="sum-charges" />
            <Stat label="Unrealised P&L" value={s.unpriced_open_trades && s.unpriced_open_trades === s.open_trades ? "—" : signed(s.unrealised_pnl)}
              cls={pnlCls(s.unrealised_pnl)} title={s.unpriced_open_trades ? `${s.unpriced_open_trades} open trade(s) have no live price right now` : undefined}
              sub={`${s.open_trades} open trade${s.open_trades === 1 ? "" : "s"} · now`} testId="sum-unrealised" />
            <Stat label="Peak deployed" value={inr(s.peak_deployed_capital ?? s.capital_used)}
              sub={`Most capital allocated at one time · in use now ${inr(s.capital_in_use_now)}`}
              title={`Sum of every trade's allocation over the period: ${inr(s.capital_used)}`} testId="sum-capital" />
            <Stat label="Return" value={(s.return_on_peak_pct ?? s.return_pct) == null ? "—" : `${(s.return_on_peak_pct ?? s.return_pct) > 0 ? "+" : ""}${num(s.return_on_peak_pct ?? s.return_pct, 2)}%`}
              cls={pnlCls(s.return_on_peak_pct ?? s.return_pct)} sub="Total P&L ÷ peak deployed capital" testId="sum-return" />
            <Stat label="Completed trades" value={s.completed_trades}
              sub={<><span className="text-[#00C805]">{s.winning_trades} won</span> · <span className="text-[#FF6b62]">{s.losing_trades} lost</span>{s.breakeven_trades ? ` · ${s.breakeven_trades} flat` : ""}</>} testId="sum-completed" />
            <Stat label="Win rate" value={s.win_rate_pct == null ? "—" : `${num(s.win_rate_pct, 1)}%`} testId="sum-winrate" />
            <Stat label={<span className="inline-flex items-center gap-1"><Trophy className="h-3 w-3" /> Best trade</span>}
              value={s.best_trade ? signed(s.best_trade.net_pnl) : "—"} cls={pnlCls(s.best_trade?.net_pnl)}
              sub={s.best_trade ? `${s.best_trade.trade_name} · ${s.best_trade.underlying}` : undefined} testId="sum-best" />
            <Stat label={<span className="inline-flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Worst trade</span>}
              value={s.worst_trade ? signed(s.worst_trade.net_pnl) : "—"} cls={pnlCls(s.worst_trade?.net_pnl)}
              sub={s.worst_trade ? `${s.worst_trade.trade_name} · ${s.worst_trade.underlying}` : undefined} testId="sum-worst" />
            <Stat label="Realised P&L" value={signed(s.realised_pnl)} cls={pnlCls(s.realised_pnl)} sub="Completed cycles (net)" testId="sum-realised" />
            <Stat label="Reconciled" value={`${s.reconciled_trades} / ${s.completed_trades}`}
              cls={s.unreconciled_trades ? "text-[#FF6b62]" : "text-[#00C805]"}
              sub={s.unreconciled_trades ? `${s.unreconciled_trades} need attention` : "Orders, fills & P&L agree"} testId="sum-reconciled" />
            <Stat label="Open positions" value={data.open_positions.count}
              sub={data.open_positions.count ? `Unrealised ${inrPnl(data.open_positions.unrealised_pnl, 0)}` : "Flat"} testId="sum-open" />
          </div>

          {data.analytics && <>
            <EquityChart points={data.analytics.equity} />
            <AnalyticsStrip a={data.analytics} Stat={Stat} />
          </>}

          {/* breakdown selector */}
          <div className="flex items-center gap-2 pt-1">
            <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]" data-testid="pnl-view">
              {[["cycle", `By trade cycle (${data.cycles.length})`], ["day", `By day (${data.by_day.length})`], ["underlying", `By underlying (${data.by_underlying.length})`],
                ...(data.analytics ? [["strategy", "By strategy"], ["exit", "By exit reason"]] : [])].map(([k, label]) => (
                <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
                  className={`px-2.5 py-1 text-[11px] font-bold ${view === k ? "bg-[#2563EB] text-white" : "bg-[#0A0C10] text-slate-500 hover:text-slate-200"}`}>{label}</button>
              ))}
            </div>
            {data.notes?.charges && <span className="text-[10px] text-slate-500">{data.notes.charges}</span>}
          </div>

          {view === "cycle" && <CyclesTable cycles={data.cycles} />}
          {view === "day" && <GroupTable testId="pnl-by-day" rows={data.by_day} first={[{ label: "Date (IST)", render: (r) => istDate(r.date) }]} />}
          {view === "underlying" && <GroupTable testId="pnl-by-underlying" rows={data.by_underlying}
            first={[{ label: "Underlying", render: (r) => r.underlying }, { label: "Market", render: (r) => r.market }]} />}
          {view === "strategy" && <BreakdownTable rows={data.analytics?.by_strategy} label="Strategy" testId="pnl-by-strategy" />}
          {view === "exit" && <BreakdownTable rows={data.analytics?.by_exit_reason} label="Exit reason" exitCodes testId="pnl-by-exit" />}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Positions tab */
const STATUS_PILL = {
  RUNNING: "bg-[#00C805] text-black", EXITING: "bg-[#FF9500] text-black", STOPPED: "bg-[#FF3B30] text-white",
  READY: "bg-[#2563EB] text-white", COMPLETE: "bg-[#7c3aed] text-white", RESETTING: "bg-[#FF9500] text-black",
};

function PositionRow({ p, onChanged }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const busy = confirming;
  // The same confirmation as the trade card: preview, explicit confirm, backend-enforced.
  const squareOff = () => setConfirming(true);
  const td = "px-2 py-1 mono whitespace-nowrap";
  return (
    <>
      <tr className="border-t-2 border-[#2A2F3A] bg-[#12151C]" data-testid={`position-${p.trade_instance_id}`}>
        <td className="pl-2 w-6 cursor-pointer text-slate-500" onClick={() => setOpen(!open)}>
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </td>
        <td className="px-2 py-1.5 whitespace-nowrap" colSpan={2}>
          <span className="font-bold text-fg">{p.trade_name}</span> <span className="text-slate-500">·</span> <span className="mono font-bold text-slate-100">{p.underlying}</span>
          {(() => { const st = futuresTradeState(p); return (
            <span title={st.title} data-testid={`position-state-${p.trade_instance_id}`}
              className={`ml-2 text-[10px] font-extrabold px-1.5 py-0.5 rounded-sm ${toneClass(st.tone)}`}>{st.label}</span>); })()}
          <EnvTag env={p.environment} />
          <span className={`ml-1 text-[9px] font-bold px-1 py-0.5 rounded-sm ${p.market === "NFO" ? "bg-[#5b9bff]/15 text-[#5b9bff]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>{p.market}</span>
          <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#7c3aed]/25 text-[#c4b5fd]">{p.execution_granularity} ENTRY</span>
          <span className={`ml-1 text-[9px] font-bold px-1 py-0.5 rounded-sm ${p.auto_square_off ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#2A2F3A] text-slate-400"}`}>AUTO SQ OFF {p.auto_square_off ? "ON" : "OFF"}</span>
          {p.exit_pending && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#FF9500]/20 text-[#FF9500]" data-testid="exit-pending">SQUARE-OFF PENDING</span>}
          {!p.pnl_consistent && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#FF3B30]/20 text-[#FF6b62]" title="Leg P&L does not add up to the trade's unrealised P&L">P&L MISMATCH</span>}
        </td>
        <td className={`${td} text-right text-slate-200`} title="Open pairs · levels">
          {p.open_pairs != null ? `${p.open_pairs} pairs` : "—"} · L{p.open_levels}{p.filling_levels ? ` +${p.filling_levels} filling` : ""}
        </td>
        <td className={`${td} text-right`} title="Quantity-weighted entry spread → live spread">
          {num(p.entry_spread_avg, 2)} → <span className="text-fg font-semibold">{num(p.current_spread, 2)}</span>
        </td>
        <td className={`${td} text-right text-slate-400`} colSpan={2}>Used {inr(p.capital_used)} · target {inr(p.target_pnl)}</td>
        <td className={`${td} text-right font-bold ${pnlCls(p.unrealised_pnl)}`} data-testid="position-unrealised"
          title={p.price_available === false ? "No live price for this contract right now (market closed or no tick) — P&L shows once prices arrive" : undefined}>
          {p.price_available === false ? <span className="text-slate-500 font-normal text-[10px]">no price</span> : signed(p.unrealised_pnl)}</td>
        <td className="px-2 py-1 text-right whitespace-nowrap">
          <button onClick={squareOff} disabled={busy || p.exit_pending} data-testid="position-sqoff"
            className="text-[10px] font-bold px-2 py-0.5 rounded-sm bg-[#FFCC00] text-black disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed">Square Off</button>
          <button onClick={() => nav(`/paper?open=${p.trade_instance_id}`)}
            className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] text-slate-300">Open</button>
          {confirming && (
            <ConfirmSquareOffDialog tradeId={p.trade_instance_id} tradeName={p.trade_name}
              onClose={() => setConfirming(false)} onDone={onChanged} />
          )}
        </td>
      </tr>
      {p.legs.map((l) => (
        <tr key={l.side} className="gz-row" data-testid={`leg-${l.side}`}>
          <td />
          <td className={`${td} pl-4`}>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${l.side === "SELL" ? "bg-[#FF6b62]/15 text-[#FF6b62]" : "bg-[#5b9bff]/15 text-[#5b9bff]"}`}>{l.side}</span>
            <span className="ml-2 text-slate-100">{l.symbol?.split(":").pop()}</span>
            <span className="ml-1 text-[9px] text-slate-500">{l.role}</span>
          </td>
          <td className={`${td} text-right ${l.net_qty < 0 ? "text-[#FF6b62]" : "text-[#5b9bff]"}`} colSpan={2} data-testid="leg-qty"
            title={l.units && l.units !== 1 ? `broker quantity ${l.net_qty} × ${l.units} units` : undefined}>{formatLots(l.qty ?? l.net_qty, l.lot_size)}</td>
          <td className={`${td} text-right text-slate-300`}>{num(l.avg_entry, 2)}</td>
          <td className={`${td} text-right text-slate-500`}>avg</td>
          <td className={`${td} text-right text-fg`}>{num(l.ltp, 2)}</td>
          <td className={`${td} text-right ${pnlCls(l.pnl)}`}>{signed(l.pnl)}</td>
          <td />
        </tr>
      ))}
      {open && (
        <tr className="bg-[#0A0C10]">
          <td />
          <td colSpan={8} className="px-2 py-1.5">
            <table className="w-full text-[10px]" data-testid="position-levels">
              <thead className="text-slate-500 uppercase tracking-wide">
                <tr>{["Level", "Status", "Pairs", "Qty", "Sell avg", "Buy avg", "Entry spread", "Trigger", "Sell LTP", "Buy LTP", "Level P&L"].map((h) => <th key={h} className="px-1.5 py-0.5 text-right first:text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {p.levels.map((lv) => (
                  <tr key={lv.level} className="gz-row mono">
                    <td className="px-1.5 py-0.5 text-slate-200">L{lv.level}</td>
                    <td className={`px-1.5 py-0.5 text-right ${lv.status === "OPEN" ? "text-[#00C805]" : "text-[#FF9500]"}`}>{lv.status === "ENTERING" ? "FILLING" : lv.status}</td>
                    <td className="px-1.5 py-0.5 text-right">{lv.pairs != null ? `${lv.pairs}${lv.status === "ENTERING" && lv.pairs_required ? `/${lv.pairs_required}` : ""}` : (lv.lots ?? "—")}</td>
                    <td className="px-1.5 py-0.5 text-right">{lv.qty}</td>
                    <td className="px-1.5 py-0.5 text-right">{num(lv.sell_avg, 2)}</td>
                    <td className="px-1.5 py-0.5 text-right">{num(lv.buy_avg, 2)}</td>
                    <td className="px-1.5 py-0.5 text-right">{num(lv.entry_spread, 2)}</td>
                    <td className="px-1.5 py-0.5 text-right text-slate-400">{num(lv.trigger_spread, 2)}</td>
                    <td className="px-1.5 py-0.5 text-right">{num(lv.sell_ltp, 2)}</td>
                    <td className="px-1.5 py-0.5 text-right">{num(lv.buy_ltp, 2)}</td>
                    <td className={`px-1.5 py-0.5 text-right font-semibold ${pnlCls(lv.level_pnl)}`}>{lv.level_pnl == null ? "after fill" : signed(lv.level_pnl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function OptionPositionRow({ p }) {
  const nav = useNavigate();
  const [openLeg, setOpenLeg] = useState(null);
  const td = "px-2 py-1 mono whitespace-nowrap";
  return (
    <>
      <tr className="border-t-2 border-[#2A2F3A] bg-[#12151C]" data-testid={`position-${p.trade_instance_id}`}>
        <td className="pl-2 w-6" />
        <td className="px-2 py-1.5 whitespace-nowrap" colSpan={2}>
          <span className="font-bold text-fg" title={p.display_name && p.display_name !== p.trade_name ? `Created as ${p.trade_name}` : undefined}>{p.display_name || p.trade_name}</span> <span className="text-slate-500">·</span> <span className="mono font-bold text-slate-100">{p.underlying}</span>
          <span className={`ml-2 text-[10px] font-extrabold px-1.5 py-0.5 rounded-sm ${STATUS_PILL[p.status] || "bg-[#475569] text-white"}`}>{p.status}</span>
          <EnvTag env={p.environment} />
          <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#5b9bff]/15 text-[#5b9bff]">{p.market}</span>
          {p.direction === "SELL" && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#FF6b62]/15 text-[#FF6b62]" data-testid="position-sell-options">SELL OPTIONS</span>}
          {p.needs_attention && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#FF3B30]/20 text-[#FF6b62]">NEEDS ATTENTION</span>}
          {p.exit_pending && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#FF9500]/20 text-[#FF9500]">EXITING</span>}
        </td>
        <td className={`${td} text-right text-slate-200`}>{p.strategy ? `${p.open_lots} lot${p.open_lots === 1 ? "" : "s"}` : `${p.open_pairs} pairs`}</td>
        <td className={`${td} text-right text-slate-500`} title={p.unrealised_basis}>{p.strategy ? (p.strategy === "BOX" ? "liquidation" : "est. close") : p.direction === "SELL" ? "ask basis" : "bid basis"}</td>
        <td className={`${td} text-right text-slate-400`} colSpan={2} data-testid="option-capital">Used {inr(p.capital_used)} · allocated {inr(p.capital_allocated)}</td>
        {(() => { const h = pnlHeadline(p.price_state, p.unrealised_pnl, p.unrealised_mark); return (
        <td className={`${td} text-right font-bold ${pnlCls(h.value)}`} data-testid="position-unrealised" title={h.title}>{signed(h.value)}
          {h.closed && <div className="text-[9px] font-normal text-slate-500" data-testid="position-executable">last prices · close now {signed(p.unrealised_pnl)}</div>}
          {p.valuation && <div className="text-[9px] font-bold text-[#B45309]" data-testid="position-valuation"
            title="The open short quantity exceeds the visible asks: the unseen part is valued at the worst visible ask">{p.valuation}</div>}</td>); })()}
        <td className="px-2 py-1 text-right whitespace-nowrap">
          <button onClick={() => nav(p.open_path)} data-testid="position-open-hedge"
            className="text-[10px] font-bold px-2 py-0.5 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] text-slate-300">Manage</button>
        </td>
      </tr>
      {p.legs.map((l) => (
        <React.Fragment key={l.symbol || l.role}>
        <tr className="gz-row" data-testid={`leg-${l.role}`}>
          <td />
          <td className={`${td} pl-4`}>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${l.side === "SELL" ? "bg-[#FF6b62]/15 text-[#FF6b62]" : "bg-[#5b9bff]/15 text-[#5b9bff]"}`}>{l.side || "BUY"}</span>
            <span className="ml-2 text-slate-100">{l.symbol?.split(":").pop()}</span>
            <span className="ml-1 text-[9px] text-slate-500">{l.role}</span>
            {(l.pairs || []).length > 1 && <button type="button" className="ml-2 text-[9px] underline text-slate-400" data-testid="leg-pairs-toggle"
              onClick={() => setOpenLeg(openLeg === l.symbol ? null : l.symbol)}>{l.pairs.length} pairs {openLeg === l.symbol ? "▾" : "▸"}</button>}
          </td>
          <td className={`${td} text-right ${l.net_qty < 0 ? "text-[#FF6b62]" : "text-[#5b9bff]"}`} colSpan={2} data-testid="leg-qty"
            title={l.units && l.units !== 1 ? `broker quantity ${l.net_qty} × ${l.units} units` : undefined}>{formatLots(l.qty ?? l.net_qty, l.lot_size)}</td>
          <td className={`${td} text-right text-slate-300`}>{num(l.avg_entry, 2)}</td>
          <td className={`${td} text-right text-slate-500`}>avg</td>
          <td className={`${td} text-right text-fg`} title={l.price_basis || "The current bid (what selling the leg now would get)"}>{num(l.ltp, 2)}
            <span className="ml-1 text-[9px] text-slate-500" data-testid="leg-price-basis">{l.side === "SELL" ? "buy-back VWAP" : "bid"}</span></td>
          <td className={`${td} text-right ${l.pnl == null ? "text-slate-600" : pnlCls(l.pnl)}`} data-testid="leg-pnl"
            title={l.pnl == null ? (p.strategy ? "Marked as a whole, not per leg" : "No price for this leg") : `${l.pnl_basis} — the trade total also takes off charges`}>{signed(l.pnl)}</td>
          <td />
        </tr>
        {openLeg === l.symbol && (l.pairs || []).map((pp, i) => (
          <tr key={`${l.symbol}-${i}`} className="text-[10px] text-slate-500" data-testid="leg-pair-detail">
            <td /><td className={`${td} pl-10`}>pair holding</td>
            <td className={`${td} text-right`}>{l.side === "SELL" ? "-" : "+"}{pp.qty}</td><td />
            <td className={`${td} text-right`}>{num(pp.avg_entry, 2)}</td><td className={`${td} text-right`}>avg</td><td /><td /><td />
          </tr>))}
        </React.Fragment>
      ))}
    </>
  );
}

function EnvTag({ env }) {
  if (!env) return null;
  const cls = env === "LIVE" ? "bg-[#FF3B30]/20 text-[#FF6b62]" : "bg-[#7c3aed]/15 text-[#c4b5fd]";
  return <span className={`ml-1 text-[9px] font-extrabold px-1 py-0.5 rounded-sm ${cls}`} data-testid="env-tag" title={modeTitle(env)}>{modeLabel(env).toUpperCase()}</span>;
}

/* Zerodha's OWN net positions for the connected account: what the broker holds, including anything
   held outside GroundZero. Display only; GroundZero never trades from this table. */
function BrokerPositions() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/zerodha/positions").then((r) => setD(r.data)).catch(() => setD({ status: "UNAVAILABLE" })), []);
  useEffect(() => { load(); return visibleInterval(load, 10000); }, [load]);
  const net = ((d?.positions || {}).net || []).filter((p) => p.quantity);
  const sync = d?.sync || {};
  const asOf = sync.last_sync_at || sync.synced_at || sync.at;
  return (
    <div className="space-y-1.5" data-testid="broker-positions">
      <div className="flex items-center gap-2 text-[11px]">
        <span className="font-bold text-fg">Zerodha positions</span>
        <span className="text-slate-500">— the broker's own net positions for your connected account, including any held outside GroundZero</span>
        <span className={`ml-auto mono ${sync.stale ? "text-[#FF6b62]" : "text-slate-500"}`} data-testid="broker-positions-asof">
          {d?.status !== "CONNECTED" ? `Zerodha ${d?.status || "…"}` : `as of ${asOf ? new Date(asOf).toLocaleTimeString("en-IN") : "—"}${sync.stale ? " · STALE" : ""}`}
        </span>
      </div>
      {d?.status === "CONNECTED" && (
        <div className="overflow-auto rounded-sm border border-[#2A2F3A]">
          <table className="w-full text-[11px] mono">
            <thead className="bg-[#12151C] text-[10px] uppercase tracking-wide text-slate-500"><tr>
              {["Instrument", "Product", "Net qty", "Avg", "LTP", "P&L"].map((h, i) => <th key={h} className={`px-2 py-1.5 ${i ? "text-right" : "text-left"}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {net.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-600">Zerodha reports no open positions.</td></tr>}
              {net.map((p) => (
                <tr key={`${p.exchange}:${p.tradingsymbol}:${p.product}`} className="gz-row" data-testid={`broker-pos-${p.tradingsymbol}`}>
                  <td className="px-2 py-1 text-slate-100">{p.exchange}:{p.tradingsymbol}</td>
                  <td className="px-2 py-1 text-right text-slate-500">{p.product}</td>
                  <td className={`px-2 py-1 text-right ${p.quantity < 0 ? "text-[#FF6b62]" : "text-[#5b9bff]"}`}>{p.quantity > 0 ? "+" : ""}{p.quantity}</td>
                  <td className="px-2 py-1 text-right">{num(p.average_price, 2)}</td>
                  <td className="px-2 py-1 text-right">{num(p.last_price, 2)}</td>
                  <td className={`px-2 py-1 text-right ${pnlCls(p.pnl)}`}>{signed(p.pnl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PositionsTab({ scope }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [market, setMarket] = useState("ALL");
  const [source, setSource] = useState("STRATEGY");
  const load = useCallback(() => api.get("/paper-book/positions", { params: { market, ...scopeParams(scope) } })
    .then((r) => { setData(r.data); setError(null); })
    .catch((e) => setError(detailText(e, "Could not load positions"))), [market, scope.product, scope.environment]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); return visibleInterval(load, 3000); }, [load]);
  const t = data?.totals;
  return (
    <div className="space-y-2" data-testid="positions-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]" data-testid="positions-source">
          {[["STRATEGY", "GroundZero positions"], ["BROKER", "Zerodha positions"]].map(([k, l]) => (
            <button key={k} onClick={() => setSource(k)} data-testid={`positions-source-${k.toLowerCase()}`}
              className={`px-2.5 py-1 text-[11px] font-bold ${source === k ? "bg-[#2563EB] text-white" : "bg-[#0A0C10] text-slate-500 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      {source === "BROKER" ? <BrokerPositions /> : <>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]">
          {["ALL", "MCX", "NFO"].map((m) => (
            <button key={m} onClick={() => setMarket(m)} className={`px-2.5 py-1 text-[11px] font-bold ${market === m ? "bg-[#2563EB] text-white" : "bg-[#0A0C10] text-slate-500 hover:text-slate-200"}`}>{m === "ALL" ? "All markets" : m}</button>
          ))}
        </div>
        <span className="text-[10px] text-slate-500">Your GroundZero strategy trades holding a position · refreshes every 3s · Futures P&L is the trade's own mark-to-market, Options P&L is net of charges, BUY OPTIONS at the bid and SELL OPTIONS at the ask (the cost to buy back)</span>
      </div>
      {error && <div className="text-[11px] text-[#FF6b62]">{error}</div>}
      {t && (
        <div className="grid gap-1.5 grid-cols-2 sm:grid-cols-5" data-testid="positions-summary">
          <Stat big label="Unrealised P&L" value={t.unpriced_trades && t.unpriced_trades === t.open_trades ? "—" : signed(t.unrealised_pnl)}
            cls={pnlCls(t.unrealised_pnl)} sub={t.unpriced_trades ? `${t.unpriced_trades} trade(s) without a live price` : undefined} />
          <Stat label="Open trades" value={t.open_trades} sub={t.exit_pending ? `${t.exit_pending} square-off pending` : undefined} />
          <Stat label="Open pairs" value={t.open_pairs} sub={`${t.open_levels} open levels`} />
          <Stat label="Capital used" value={inr(t.capital_used)} sub={`of ${inr(t.capital_allocated)} allocated`} />
          <Stat label="Capital free" value={inr((t.capital_allocated ?? 0) - (t.capital_used ?? 0))} sub="allocated − used" />
        </div>
      )}
      {data && (
        <div className="overflow-auto rounded-sm border border-[#2A2F3A]" data-testid="positions-table">
          <table className="w-full text-[11px]">
            <thead className="bg-[#12151C] text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-6" /><th className="px-2 py-1.5 text-left">Instrument</th><th className="px-2 py-1.5 text-right">Net qty</th>
                <th className="px-2 py-1.5 text-right">Pairs / levels</th><th className="px-2 py-1.5 text-right">Avg entry · spread</th>
                <th className="px-2 py-1.5 text-right" /><th className="px-2 py-1.5 text-right">LTP</th>
                <th className="px-2 py-1.5 text-right">Unrealised P&L</th><th className="px-2 py-1.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.positions.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-600" data-testid="positions-empty">No open positions. Completed trades are in P&L and History.</td></tr>
              )}
              {data.positions.map((p) => (p.product === "OPTIONS"
                ? <OptionPositionRow key={p.trade_instance_id} p={p} />
                : <PositionRow key={p.trade_instance_id} p={p} onChanged={load} />))}
            </tbody>
          </table>
        </div>
      )}
      </>}
    </div>
  );
}


export default function PaperBook() {
  const [params, setParams] = useSearchParams();
  const scope = useBookScope();
  const tab = params.get("tab") || "pnl";
  const go = (k) => { const n = new URLSearchParams(params); n.set("tab", k); setParams(n, { replace: true }); };
  return (
    <div className="p-3 space-y-2" data-testid="paper-book">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-lg font-bold tracking-tight flex items-center gap-2"><BookOpen className="h-5 w-5 text-[#2563EB]" /> Trading Book</h1>
        <BookScope scope={scope} />
        <nav role="tablist" className="flex items-center gap-0.5 border-b border-line" data-testid="book-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} role="tab" aria-selected={tab === key} onClick={() => go(key)} data-testid={`book-tab-${key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border-b-2 -mb-px ${tab === key ? "border-[var(--gz-blue)] text-fg" : "border-transparent text-fg-secondary hover:text-fg hover:bg-hover"}`}>
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </nav>
      </div>
      {tab === "pnl" && <PnlTab scope={scope} />}
      {tab === "positions" && <PositionsTab scope={scope} />}
      {tab === "orders" && <OrderBook embedded scope={scope} />}
      {tab === "tradebook" && <TradeBook embedded initialView="fills" key="fills" scope={scope} />}
      {tab === "history" && <TradeBook embedded initialView="cycles" key="cycles" scope={scope} />}
    </div>
  );
}
